import { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { postHogService } from '../../application/PostHogService';
import { DriversLayout } from '../../components/DriversLayout';
import { LoaderPage } from '../../components/LoaderPage';
import { MobileAppBanner } from '../../components/MobileAppBanner';
import * as routing from '../../constants/Routing';
import { useIsLogged } from '../../hooks/session/useIsLogged';
import { intercom } from '../../services/intercom';
import { Analytics } from './analytics';
import { NotificationDialog } from './components/NotificationDialog';
import { UpgradeSubscriptionComponent } from './components/upgradeSubscription/UpgradeSubscriptionComponent';
import { Home } from './home/Home';
import { usePermissions } from './hooks/usePermissions';
import { useRetrieveDriverCredentials } from './hooks/useRetrieveDriverCredentials';
import { useRetrieveRevenueCatCustomer } from './hooks/useRetrieveRevenueCatCustomer';
import { useRetrieveVehicle } from './hooks/useRetrieveVehicle';
import { useVehicleWebSockets } from './hooks/useVehicleWebSockets';
import { RoutesList } from './routes-list';

export const PRIVATE_COMPONENT_ID = `private-component`;

export const PrivateComponent = () => {
  const { isSoloDriver, hasSubscription } = usePermissions();
  const { isLogged, isLoading: isLoadingLogged } = useIsLogged();
  const { error: retrieveVehicleError, isLoading: isLoadingVehicle, data: vehicleData } = useRetrieveVehicle();
  const { error: credentialsError, isLoading: isLoadingCredentials } = useRetrieveDriverCredentials();
  useRetrieveRevenueCatCustomer();
  useVehicleWebSockets();
  useEffect(() => {
    if (!vehicleData?.vehicle) return;

    // Intercom stuff (start)
    intercom.setUser({
      userId: vehicleData.vehicle.id,
      email: isSoloDriver && vehicleData.vehicle.email ? vehicleData.vehicle.email : ``,
    });

    intercom.updateUser({
      userId: vehicleData.vehicle.id,
      email: isSoloDriver && vehicleData.vehicle.email ? vehicleData.vehicle.email : ``,
      name: vehicleData.vehicle.label,
      phone: vehicleData.vehicle.phone ?? ``,
      customAttributes: {
        solo_driver: isSoloDriver,
        company: {
          company_id: vehicleData.vehicle.organization_id,
          name: vehicleData.vehicle.label,
          created_at: vehicleData.vehicle.updated_at,
          plan: isSoloDriver ? (hasSubscription ? `drivers-lite` : `drivers-free`) : `drivers-planner`,
          status: `active`,
        },
      },
    });
    // Intercom stuff (end)

    // PostHog stuff (start)
    postHogService.identify(vehicleData.vehicle.id, {
      email: isSoloDriver && vehicleData.vehicle.email ? vehicleData.vehicle.email : ``,
      name: vehicleData.vehicle.label,
      phone: vehicleData.vehicle.phone ?? ``,
      [`Project Id`]: vehicleData?.vehicle.project_id,
      [`App Version`]: `v3`,
      [`Company Id`]: vehicleData.vehicle.organization_id,
      [`Company Name`]: vehicleData.vehicle.label,
      [`Subscription Plan`]: isSoloDriver ? (hasSubscription ? `drivers-lite` : `drivers-free`) : `drivers-planner`,
      [`Subscription Status`]: `active`,
      [`Is Solo Driver`]: isSoloDriver,
      // [`Signed Up At`]: vehicleData.vehicle.created_at,
    });
    // PostHog stuff (end)
  }, [vehicleData?.vehicle, isSoloDriver, hasSubscription]);

  return (
    <DriversLayout id={PRIVATE_COMPONENT_ID}>
      {/* Redirects */}
      <>
        {((!isLoadingLogged && !isLogged) || retrieveVehicleError || credentialsError) && (
          <Navigate to={routing.AUTH} />
        )}
      </>

      {/* Overlays */}
      <>
        {(isLoadingLogged || isLoadingVehicle || isLoadingCredentials) && <LoaderPage />}
        <MobileAppBanner />
        <NotificationDialog />
        <UpgradeSubscriptionComponent />
      </>

      {/* Routes */}
      <Routes>
        <Route path={`${routing.PRIVATE_ROUTES.ROUTES}/*`} element={<RoutesList />} />
        <Route path={`${routing.PRIVATE_ROUTES.ROOT}/*`} element={<Home />} />
        <Route path={`${routing.PRIVATE_ROUTES.ANALYTICS}/*`} element={<Analytics />} />
        <Route path={routing.NOMATCHES} element={<Navigate replace to={routing.PRIVATE_ROUTES.ROOT} />} />
      </Routes>
    </DriversLayout>
  );
};
